import { useState, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import { useApi } from "../hooks/use-api";
import { useWedding } from "../contexts/wedding-context";
import { queryKeys } from "../lib/query-keys";
import {
  ACCEPTED_MEDIA_TYPES,
  MAX_IMAGE_SIZE,
  MAX_VIDEO_SIZE,
  MAX_VIDEO_SIZE_DIRECT,
  stripGpsFromBlob,
} from "@wedding/shared";
import {
  Camera,
  Loader2,
  CheckCircle2,
  XCircle,
  X,
  Info,
  Lightbulb,
  AlertCircle,
  AlertTriangle,
  Film,
  ImageIcon,
} from "lucide-react";
import { cn } from "../lib/utils";
import { MyUploads } from "../components/my-uploads";

type FileStatus = "pending" | "uploading" | "done" | "error";

interface QueueItem {
  id: string;
  file: File;
  previewUrl: string;
  status: FileStatus;
  error?: string;
  /** 0–100 upload percent while status === "uploading". */
  progress?: number;
}

interface VideoWarning {
  itemId: string;
  message: string;
}

// Long clips are more likely to stall or exceed MAX_VIDEO_SIZE on weak venue
// WiFi; warn (don't block — the real gate is file size, checked separately).
const VIDEO_WARN_DURATION_SECONDS = 60;

// Reads duration from local file metadata only (no network fetch — safe for
// any file size, unlike rendering a full <video> preview element).
function getVideoDuration(file: File): Promise<number | null> {
  return new Promise(resolve => {
    const url = URL.createObjectURL(file);
    const video = document.createElement("video");
    video.preload = "metadata";
    video.src = url;
    const cleanup = () => URL.revokeObjectURL(url);
    video.onloadedmetadata = () => {
      const duration = video.duration;
      cleanup();
      resolve(Number.isFinite(duration) ? duration : null);
    };
    video.onerror = () => {
      cleanup();
      resolve(null);
    };
  });
}

// Uploads `items` with bounded concurrency instead of strictly one-at-a-time,
// so a slow/large item doesn't stall the rest of the batch behind it.
async function runWithConcurrency<T>(
  items: T[],
  limit: number,
  worker: (item: T) => Promise<void>,
): Promise<void> {
  let index = 0;
  async function next(): Promise<void> {
    while (index < items.length) {
      const item = items[index++];
      await worker(item);
    }
  }
  await Promise.all(Array.from({ length: Math.min(limit, items.length) }, next));
}

export function UploadPage() {
  const { t } = useTranslation();
  const api = useApi();
  const queryClient = useQueryClient();
  const { slug, wedding } = useWedding();
  // Effective media size limits from /info (runtime-configurable server-side).
  // Advisory only — the server re-checks and is authoritative. Fall back to the
  // shared compile-time constants when absent (older API / not yet loaded).
  const maxImageBytes = wedding?.limits?.maxImageBytes ?? MAX_IMAGE_SIZE;
  const maxVideoBytes = wedding?.limits?.maxVideoDirectBytes ?? MAX_VIDEO_SIZE_DIRECT;
  const inputRef = useRef<HTMLInputElement>(null);
  const [queue, setQueue] = useState<QueueItem[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitDone, setSubmitDone] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [rejectedErrors, setRejectedErrors] = useState<string[]>([]);
  const [videoWarnings, setVideoWarnings] = useState<VideoWarning[]>([]);
  // Ids removed from the queue before their async duration-probe resolved —
  // checked so a stale warning can't reappear after the file is already gone.
  const removedIdsRef = useRef<Set<string>>(new Set());

  const addFiles = useCallback(
    (files: FileList | null) => {
      if (!files || isSubmitting) return;
      const newItems: QueueItem[] = [];
      const errors: string[] = [];
      Array.from(files).forEach(file => {
        if (!ACCEPTED_MEDIA_TYPES.includes(file.type as never)) {
          errors.push(t("media.errorUnsupportedType", { name: file.name }));
          return;
        }
        const maxSize = file.type.startsWith("video/") ? maxVideoBytes : maxImageBytes;
        if (file.size > maxSize) {
          const limitMb = Math.round(maxSize / 1024 / 1024);
          errors.push(t("media.errorTooLarge", { name: file.name, limit: limitMb }));
          return;
        }
        newItems.push({
          id: crypto.randomUUID(),
          file,
          previewUrl: URL.createObjectURL(file),
          status: "pending",
        });
      });
      setRejectedErrors(errors);
      if (newItems.length) {
        setQueue(prev => [...prev, ...newItems]);
        setSubmitDone(false);
        newItems
          .filter(item => item.file.type.startsWith("video/"))
          .forEach(item => {
            getVideoDuration(item.file).then(duration => {
              if (duration && duration > VIDEO_WARN_DURATION_SECONDS && !removedIdsRef.current.has(item.id)) {
                setVideoWarnings(prev => [
                  ...prev,
                  { itemId: item.id, message: t("media.videoLongWarning", { name: item.file.name, duration: Math.round(duration) }) },
                ]);
              }
            });
          });
      }
    },
    [isSubmitting, t, maxImageBytes, maxVideoBytes],
  );

  const removeItem = useCallback((id: string) => {
    removedIdsRef.current.add(id);
    setQueue(prev => {
      const item = prev.find(i => i.id === id);
      if (item) URL.revokeObjectURL(item.previewUrl);
      return prev.filter(i => i.id !== id);
    });
    setVideoWarnings(prev => prev.filter(w => w.itemId !== id));
  }, []);

  const clearAll = useCallback(() => {
    setQueue(prev => {
      prev.forEach(i => {
        removedIdsRef.current.add(i.id);
        URL.revokeObjectURL(i.previewUrl);
      });
      return [];
    });
    setVideoWarnings([]);
    setSubmitDone(false);
  }, []);

  const retryFailed = useCallback(() => {
    setQueue(prev =>
      prev.map(i =>
        i.status === "error" ? { ...i, status: "pending" as const, error: undefined } : i,
      ),
    );
    setSubmitDone(false);
  }, []);

  const UPLOAD_CONCURRENCY = 3;

  const handleSubmit = useCallback(async () => {
    const pending = queue.filter(i => i.status === "pending");
    if (!pending.length || isSubmitting) return;
    setIsSubmitting(true);
    await runWithConcurrency(pending, UPLOAD_CONCURRENCY, async item => {
      setQueue(prev => prev.map(i => i.id === item.id ? { ...i, status: "uploading", progress: 0 } : i));
      const onProgress = (pct: number) =>
        setQueue(prev => prev.map(i => i.id === item.id ? { ...i, progress: pct } : i));
      try {
        const isVideo = item.file.type.startsWith("video/");
        if (isVideo) {
          // Large videos go straight to R2 (past the Worker body limit). Safety
          // net: if presigning/direct PUT fails but the file fits the proxy cap,
          // fall back to the Worker upload so ≤95 MB videos still succeed.
          try {
            await api.uploadDirect(item.file, "video", onProgress);
          } catch (err) {
            if (item.file.size > MAX_VIDEO_SIZE) throw err;
            onProgress(0);
            const formData = new FormData();
            formData.append("file", item.file);
            await api.upload("/media/upload", formData, onProgress);
          }
        } else {
          // Konum (GPS) cihazdan çıkmadan silinir — yeniden kodlama yok,
          // piksel/yön aynı (KVKK, F9). Worker aynı işi güvenlik ağı olarak yapar.
          const { blob, removed } = await stripGpsFromBlob(item.file);
          const formData = new FormData();
          if (removed) formData.append("file", blob, item.file.name);
          else formData.append("file", item.file);
          await api.upload("/media/upload", formData, onProgress);
        }
        setQueue(prev => prev.map(i => i.id === item.id ? { ...i, status: "done" } : i));
      } catch (err) {
        setQueue(prev =>
          prev.map(i =>
            i.id === item.id ? { ...i, status: "error", error: (err as Error).message } : i,
          ),
        );
      }
    });
    setIsSubmitting(false);
    setSubmitDone(true);
    queryClient.invalidateQueries({ queryKey: queryKeys.media.all(slug) });
  }, [queue, isSubmitting, api, queryClient, slug]);

  const onDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setDragOver(false);
      addFiles(e.dataTransfer.files);
    },
    [addFiles],
  );

  const pendingCount = queue.filter(i => i.status === "pending").length;
  // HEIC'teki konum etiketi tarayıcıda silinemiyor → kullanıcıyı bilgilendir.
  const hasHeic = queue.some(i => i.status === "pending" && isHeic(i.file));
  const allDone = queue.length > 0 && queue.every(i => i.status === "done");
  const hasErrors = queue.some(i => i.status === "error");

  return (
    <div className="space-y-4 pb-8">
      {/* Page header — desktop only */}
      <div className="hidden lg:block mb-6">
        <h2 className="text-3xl font-bold text-foreground dark:text-white">{t("media.uploadTitle")}</h2>
        <p className="text-muted-foreground mt-1">{t("media.uploadDesc")}</p>
      </div>

      {/* Moderation banner */}
      <div className="flex items-start gap-3 bg-primary/10 border border-primary/20 rounded-xl p-4">
        <Info className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
        <p className="text-sm text-primary/90 font-medium">{t("media.moderationNotice")}</p>
      </div>

      {/* Rejected files banner */}
      {rejectedErrors.length > 0 && (
        <div className="flex items-start justify-between gap-3 bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/20 rounded-xl p-4">
          <div className="flex items-start gap-3 min-w-0">
            <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
            <div className="space-y-0.5 min-w-0">
              {rejectedErrors.map((err, i) => (
                <p key={i} className="text-sm text-red-700 dark:text-red-300">{err}</p>
              ))}
            </div>
          </div>
          <button
            type="button"
            onClick={() => setRejectedErrors([])}
            className="text-red-400 hover:text-red-600 transition-colors shrink-0"
            aria-label="Kapat"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* Long video warning banner (non-blocking) */}
      {videoWarnings.length > 0 && (
        <div className="flex items-start justify-between gap-3 bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-500/20 rounded-xl p-4">
          <div className="flex items-start gap-3 min-w-0">
            <AlertTriangle className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" />
            <div className="space-y-0.5 min-w-0">
              {videoWarnings.map((warning) => (
                <p key={warning.itemId} className="text-sm text-amber-700 dark:text-amber-300">{warning.message}</p>
              ))}
            </div>
          </div>
          <button
            type="button"
            onClick={() => setVideoWarnings([])}
            className="text-amber-400 hover:text-amber-600 transition-colors shrink-0 cursor-pointer"
            aria-label="Kapat"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {hasHeic && (
        <div
          data-testid="heic-location-note"
          className="flex items-start gap-3 bg-muted/60 border border-border rounded-xl p-4"
        >
          <Info className="h-5 w-5 text-muted-foreground flex-shrink-0 mt-0.5" />
          <p className="text-sm text-muted-foreground">{t("media.heicLocationNote")}</p>
        </div>
      )}

      {/* Success banner */}
      {submitDone && allDone && (
        <div className="flex items-center gap-3 bg-green-50 dark:bg-green-500/10 border border-green-200 dark:border-green-500/20 rounded-xl p-4">
          <CheckCircle2 className="h-5 w-5 text-green-600 dark:text-green-400 flex-shrink-0" />
          <p className="text-sm text-green-700 dark:text-green-300 font-medium">
            {t("media.uploadSuccess")}
          </p>
        </div>
      )}

      {/* Partial failure banner */}
      {submitDone && hasErrors && (
        <div className="flex items-center justify-between gap-3 bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/20 rounded-xl p-4">
          <div className="flex items-center gap-3">
            <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0" />
            <p className="text-sm text-red-700 dark:text-red-300 font-medium">
              {t("media.uploadPartialError")}
            </p>
          </div>
          <button
            type="button"
            onClick={retryFailed}
            className="shrink-0 text-sm font-semibold text-red-600 dark:text-red-400 hover:underline cursor-pointer"
          >
            {t("media.retryFailed")}
          </button>
        </div>
      )}

      {/* ── Mobile layout ── */}
      <div className="lg:hidden space-y-4">
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          onDrop={onDrop}
          onDragOver={e => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          disabled={isSubmitting}
          className={cn(
            "w-full flex flex-col items-center justify-center gap-4 rounded-2xl border-2 border-dashed p-10 transition-colors cursor-pointer",
            dragOver
              ? "border-primary bg-primary/10"
              : "border-primary/30 bg-primary/5 hover:border-primary/50",
          )}
        >
          <div className="w-14 h-14 bg-primary/15 rounded-full flex items-center justify-center">
            <Camera className="h-7 w-7 text-primary" />
          </div>
          <div className="text-center">
            <p className="font-semibold text-foreground dark:text-white">{t("media.dropzone")}</p>
            <p className="text-xs text-muted-foreground mt-1">{t("media.dropzoneDesc")}</p>
          </div>
        </button>

        {queue.length > 0 && (
          <div className="space-y-2">
            {queue.map(item => (
              <FileRow
                key={item.id}
                item={item}
                onRemove={removeItem}
                disabled={isSubmitting}
                size="sm"
              />
            ))}
          </div>
        )}

        {pendingCount > 0 && (
          <button
            onClick={handleSubmit}
            disabled={isSubmitting}
            className="w-full flex items-center justify-center gap-2 bg-primary text-white font-semibold py-3.5 rounded-full transition-colors hover:bg-primary/90 disabled:opacity-60 cursor-pointer disabled:cursor-not-allowed"
          >
            {isSubmitting && <Loader2 className="h-4 w-4 animate-spin" />}
            {t("media.submitBtn", { count: pendingCount })}
          </button>
        )}

        {queue.length > 0 && !isSubmitting && (
          <button
            onClick={clearAll}
            className="w-full text-sm text-primary/60 hover:text-primary py-1 transition-colors cursor-pointer"
          >
            {t("media.clearAll")}
          </button>
        )}
      </div>

      {/* ── Desktop layout ── */}
      <div className="hidden lg:grid lg:grid-cols-12 gap-8">
        {/* Left 7/12 */}
        <div className="col-span-7 flex flex-col gap-4">
          <div
            onClick={() => !isSubmitting && inputRef.current?.click()}
            onDrop={onDrop}
            onDragOver={e => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            className={cn(
              "flex flex-col items-center justify-center gap-6 rounded-3xl border-2 border-dashed p-12 text-center transition-[background-color,border-color] cursor-pointer min-h-[420px] group",
              dragOver
                ? "border-primary bg-primary/10"
                : "border-primary/30 bg-card dark:bg-white/[0.03] hover:border-primary/50 hover:bg-primary/5",
            )}
          >
            <div className="w-20 h-20 bg-primary/12 rounded-full flex items-center justify-center group-hover:scale-105 motion-reduce:group-hover:scale-100 transition-transform motion-reduce:transition-none">
              <Camera className="h-10 w-10 text-primary" />
            </div>
            <div>
              <h3 className="text-xl font-semibold text-foreground dark:text-white mb-1">
                {t("media.dropzone")}
              </h3>
              <p className="text-sm text-muted-foreground">{t("media.dropzoneDesc")}</p>
            </div>
          </div>

          <div className="bg-card dark:bg-white/[0.03] border border-black/8 dark:border-white/10 rounded-2xl p-6">
            <h4 className="font-semibold text-foreground dark:text-white mb-4 flex items-center gap-2">
              <Lightbulb className="h-4 w-4 text-primary" />
              {t("media.uploadTips")}
            </h4>
            <ul className="space-y-3 text-sm text-muted-foreground">
              {[t("media.tip1"), t("media.tip2"), t("media.tip3")].map((tip, i) => (
                <li key={i} className="flex items-start gap-2">
                  <CheckCircle2 className="h-4 w-4 text-primary/50 flex-shrink-0 mt-0.5" />
                  {tip}
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Right 5/12 */}
        <div className="col-span-5 flex flex-col">
          <div className="bg-card dark:bg-white/[0.03] border border-black/8 dark:border-white/10 rounded-3xl p-6 flex flex-col flex-1">
            <div className="flex items-center justify-between mb-5">
              <h3 className="font-semibold text-foreground dark:text-white">
                {queue.length > 0
                  ? t("media.filesSelected", { count: queue.length })
                  : t("media.noFilesSelected")}
              </h3>
              {queue.length > 0 && !isSubmitting && (
                <button
                  onClick={clearAll}
                  className="text-sm text-primary hover:text-primary/80 font-medium transition-colors cursor-pointer"
                >
                  {t("media.clearAll")}
                </button>
              )}
            </div>

            <div className="flex-1 overflow-y-auto space-y-3 min-h-[220px]">
              {queue.length === 0 ? (
                <div className="h-full flex flex-col items-center justify-center text-center py-10">
                  <Camera className="h-10 w-10 text-primary/20 mb-3" />
                  <p className="text-sm text-muted-foreground">{t("media.noFilesSelected")}</p>
                </div>
              ) : (
                queue.map(item => (
                  <FileRow
                    key={item.id}
                    item={item}
                    onRemove={removeItem}
                    disabled={isSubmitting}
                    size="lg"
                  />
                ))
              )}
            </div>

            {pendingCount > 0 && (
              <div className="mt-5 pt-5 border-t border-black/8 dark:border-white/10 space-y-2">
                <button
                  onClick={handleSubmit}
                  disabled={isSubmitting}
                  className="w-full flex items-center justify-center gap-2 bg-primary text-white font-semibold py-3.5 rounded-full transition-colors hover:bg-primary/90 disabled:opacity-60 cursor-pointer disabled:cursor-not-allowed"
                >
                  {isSubmitting && <Loader2 className="h-4 w-4 animate-spin" />}
                  {t("media.submitBtn", { count: pendingCount })}
                </button>
                <button
                  onClick={() => inputRef.current?.click()}
                  className="w-full text-sm text-primary/60 hover:text-primary py-1 transition-colors cursor-pointer"
                >
                  {t("media.addMore")}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      <MyUploads />

      <input
        ref={inputRef}
        type="file"
        accept={ACCEPTED_MEDIA_TYPES.join(",")}
        multiple
        onChange={e => addFiles(e.target.files)}
        className="hidden"
      />
    </div>
  );
}

// Returns false for file types/sizes where browser preview is unreliable:
// - image/heic: not renderable outside Safari
// - video/3gpp: poor browser support for <video> preview
// - videos > 20 MB: loading into <video> buffers the whole file, hangs on mobile
const VIDEO_PREVIEW_SIZE_LIMIT = 20 * 1024 * 1024;

function isHeic(file: File): boolean {
  return file.type === "image/heic" || file.type === "image/heif" || /\.hei[cf]$/i.test(file.name);
}

function canPreviewMedia(file: File): boolean {
  if (file.type === "image/heic") return false;
  if (file.type === "video/3gpp") return false;
  if (file.type.startsWith("video/") && file.size > VIDEO_PREVIEW_SIZE_LIMIT) return false;
  return true;
}

function FileRow({
  item,
  onRemove,
  disabled,
  size,
}: {
  item: QueueItem;
  onRemove: (id: string) => void;
  disabled: boolean;
  size: "sm" | "lg";
}) {
  const { t } = useTranslation();
  const thumbSize = size === "lg" ? "w-16 h-16" : "w-12 h-12";
  return (
    <div className="flex items-center gap-3 bg-primary/5 dark:bg-white/[0.03] border border-black/8 dark:border-white/10 rounded-xl px-3 py-2">
      <div className={cn(thumbSize, "rounded-lg overflow-hidden flex-shrink-0 bg-primary/10 flex items-center justify-center")}>
        {canPreviewMedia(item.file) ? (
          item.file.type.startsWith("video/") ? (
            <video src={item.previewUrl} className="w-full h-full object-cover" muted playsInline preload="metadata" />
          ) : (
            <img
              src={item.previewUrl}
              alt=""
              className={cn(
                "w-full h-full object-cover",
                item.status === "uploading" && "opacity-50",
              )}
            />
          )
        ) : (
          item.file.type.startsWith("video/")
            ? <Film className="h-5 w-5 text-primary/50" />
            : <ImageIcon className="h-5 w-5 text-primary/50" />
        )}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-foreground dark:text-white truncate">
          {item.file.name}
        </p>
        {item.error && (
          <p className="text-xs text-destructive truncate mt-0.5">{item.error}</p>
        )}
        {item.status === "uploading" && item.progress !== undefined && (
          <div className="mt-1.5 h-1.5 rounded-full bg-primary/15 overflow-hidden" aria-hidden>
            <div
              className="h-full bg-primary transition-[width] duration-150"
              style={{ width: `${item.progress}%` }}
            />
          </div>
        )}
      </div>
      {item.status === "uploading" && (
        (item.progress ?? 0) > 0 ? (
          <span className="text-xs font-semibold text-primary tabular-nums flex-shrink-0 w-9 text-right">
            %{item.progress}
          </span>
        ) : (
          <Loader2 className="h-4 w-4 animate-spin text-primary flex-shrink-0" />
        )
      )}
      {item.status === "done" && (
        <CheckCircle2 className="h-4 w-4 text-green-500 flex-shrink-0" />
      )}
      {item.status === "error" && (
        <XCircle className="h-4 w-4 text-destructive flex-shrink-0" />
      )}
      {item.status === "pending" && !disabled && (
        <button
          onClick={() => onRemove(item.id)}
          aria-label={t("common.remove")}
          className="h-7 w-7 flex items-center justify-center rounded-full hover:bg-primary/10 text-muted-foreground hover:text-primary transition-colors flex-shrink-0 cursor-pointer"
        >
          <X className="h-4 w-4" />
        </button>
      )}
    </div>
  );
}
